import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import {
	Confirm,
	Modal,
	SubmissionButtons,
	TextArea,
} from '@components/common';

import { useMutateSendQueryAsset } from '@hooks/assets/useMutateSendQueryAsset';

import { map } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { isEmpty } from '@utils/common';
import { currentTimeZoneInMinutes } from '@utils/timeUtils';

import { EValidationMode } from '@enums/validationMode';

import { MODAL_WIDTH_480, modalCloseConfirm } from '@constants/modals';

import { validationSchema } from './validationSchema';

export interface IAssetQueryFormValues {
	text?: string;
}

interface IQueryAssetFormProps {
	closeModal: () => void;
	setIsDirtyForm: React.Dispatch<React.SetStateAction<boolean>>;
}

const QUESTION_PLACEHOLDER = 'Describe your question about asset';
const DEFAULT_TEXTAREA_ROWS = 5;

const QueryAssetForm = ({
	closeModal,
	setIsDirtyForm,
}: IQueryAssetFormProps) => {
	const {
		register,
		handleSubmit,
		formState: { errors, isDirty },
	} = useForm<IAssetQueryFormValues>({
		mode: EValidationMode.onSubmit,
		resolver: yupResolver(validationSchema),
		defaultValues: {
			text: '',
		},
	});

	const { sendQueryAsset } = useMutateSendQueryAsset(closeModal);

	const { activeAssetMarkers, assetIdsInsidePolygons } = useAppSelector(map);

	const [isConfirmModal, setIsConfirmModal] = useState(false);

	useEffect(() => setIsDirtyForm(isDirty), [isDirty, setIsDirtyForm]);

	const onSubmit = (values: IAssetQueryFormValues) => {
		sendQueryAsset({
			assetIds: [...activeAssetMarkers, ...(assetIdsInsidePolygons || [])],
			comments: values.text,
			timeZoneMinutes: currentTimeZoneInMinutes,
		} as any);
	};

	const handleCloseConfirmModal = () => setIsConfirmModal(false);

	const handleCloseForm = () => {
		if (isDirty) setIsConfirmModal(true);
		else closeModal();
	};

	return (
		<>
			<TextArea
				errorMessage={errors?.text?.message}
				label='Comments'
				name='text'
				placeholder={QUESTION_PLACEHOLDER}
				register={register}
				required
				rows={DEFAULT_TEXTAREA_ROWS}
				withUseForms
			/>
			<SubmissionButtons
				disabledPositive={!isEmpty(errors)}
				negativeHandler={handleCloseForm}
				positiveHandler={handleSubmit(onSubmit)}
				positiveLabel='Send'
			/>
			{isConfirmModal && (
				<Modal
					isOpen={isConfirmModal}
					onClose={handleCloseConfirmModal}
					width={MODAL_WIDTH_480}
				>
					<Confirm
						onClose={handleCloseConfirmModal}
						onSubmit={closeModal}
						question={modalCloseConfirm.question}
					>
						{modalCloseConfirm.children}
					</Confirm>
				</Modal>
			)}
		</>
	);
};

export default QueryAssetForm;
